'use client';

import { type FC, type ReactNode, useCallback } from 'react';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useCurrentTime } from '../../hooks/useCurrentTime';
import { useHaptic } from '../../hooks/useHaptic';
import { pluralize } from '../../i18n/pluralize';
import { cn } from '../../utils/cn';
import { CaseCardBase } from '../cases/CaseCardBase';
import { Button } from '../ui/Button';
import { PrizeStatTile } from '../PrizeStatTile';
import { RainCaseBackdrop } from './RainCaseBackdrop';
import { RainCountdown, secondsUntil } from './RainCountdown';
import translations from './RainCard.translations';
import type { RainCardState, RainCase } from './types';

export interface RainCardProps {
  state: RainCardState;
  sourceCase: RainCase;
  startAt: string;
  endAt: string;
  participantsCount: number;
  winnerSlots: number;
  ineligibleReason?: string;
  onJoin?: () => void;
  onLogin?: () => void;
  onOpen?: () => void;
  className?: string;
}

export const RainCard: FC<RainCardProps> = ({
  state,
  sourceCase,
  startAt,
  endAt,
  participantsCount,
  winnerSlots,
  ineligibleReason,
  onJoin,
  onLogin,
  onOpen,
  className = '',
}) => {
  const { t, lang } = useLocalTranslation(translations);
  const now = useCurrentTime();
  const { impact } = useHaptic();

  const handleJoin = useCallback(() => {
    impact('medium');
    onJoin?.();
  }, [impact, onJoin]);

  if (state === 'idle') return null;

  const isOver = secondsUntil(endAt, now) === 0;

  const participantsWord = pluralize(
    participantsCount,
    { one: t('participantsOne'), few: t('participantsFew'), many: t('participantsMany') },
    lang,
  );
  const prizesWord = pluralize(
    winnerSlots,
    { one: t('prizesOne'), few: t('prizesFew'), many: t('prizesMany') },
    lang,
  );

  let actionLabel = t('join');
  if (isOver) actionLabel = t('ended');
  else if (state === 'joined') actionLabel = t('joined');
  else if (state === 'guest') actionLabel = t('login');

  const isGuest = state === 'guest' && !isOver;
  const actionDisabled =
    isOver || state === 'joined' || state === 'ineligible' || (state === 'guest' && !onLogin);
  const handleAction = isGuest ? onLogin : handleJoin;

  return (
    <div className={cn('relative overflow-hidden rounded-2xl border border-white/[0.08] text-white', className)}>
      <RainCaseBackdrop imageUrl={sourceCase.imageUrl} imagePlaceholder={sourceCase.imagePlaceholder} live />

      <div className="relative z-[1] flex flex-col gap-3 p-3">

        <TapArea onClick={onOpen}>

          <RainCountdown startAt={startAt} endAt={endAt} />

          <div className="flex items-stretch gap-3">

            <div className={cn('w-[140px] shrink-0', !onOpen && 'pointer-events-none')}>
              <CaseCardBase
                imageUrl={sourceCase.imageUrl}
                imagePlaceholder={sourceCase.imagePlaceholder}
                alt={sourceCase.name}
              />
            </div>


            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <div className="truncate text-sm font-semibold text-white">{sourceCase.name}</div>
              <PrizeStatTile className="flex-1">
                <div className="truncate text-xs font-normal text-white/70">{t('participantsLabel')}</div>
                <div className="truncate text-sm font-semibold text-white">
                  <span className="tabular-nums">{participantsCount}</span> {participantsWord}
                </div>
              </PrizeStatTile>
              <PrizeStatTile className="flex-1">
                <div className="truncate text-xs font-normal text-white/70">{t('prizesLabel')}</div>
                <div className="truncate text-sm font-semibold text-white">
                  <span className="tabular-nums">{winnerSlots}</span> {prizesWord}
                </div>
              </PrizeStatTile>
            </div>
          </div>
        </TapArea>

        <div>
          {state === 'ineligible' && ineligibleReason && (
            <div className="mb-2 rounded-lg bg-white/15 px-3 py-1.5 text-center text-sm font-medium text-white">
              {ineligibleReason}
            </div>
          )}
          <Button
            type="button"
            variant="white"
            size="l"
            stretched
            loading={state === 'pending'}
            disabled={actionDisabled}
            onClick={handleAction}
            className="h-[45px]"
          >

            <span className="block max-w-full truncate">{actionLabel}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

const TapArea: FC<{ onClick?: () => void; children: ReactNode }> = ({ onClick, children }) => {
  const box = 'flex flex-col gap-3 text-start';
  if (!onClick) return <div className={box}>{children}</div>;
  return (
    <button type="button" onClick={onClick} className={cn(box, 'w-full cursor-pointer transition active:opacity-80')}>
      {children}
    </button>
  );
};
